import React from "react";
import { Trash2 } from "lucide-react";

// A property condition (Trigger event conditions, Goal event conditions, Audience custom).
export interface Condition {
  property: string;
  operator: string;
  value: string;
}

// Property-condition builder (property / operator / value rows + Add). Used by Trigger
// event conditions, Goal event conditions, and Audience custom conditions.
export const CONDITION_OPS: [string, string][] = [
  ["eq", "equals"], ["ne", "not equals"], ["gt", "greater than"], ["lt", "less than"],
  ["gte", "≥"], ["lte", "≤"], ["contains", "contains"], ["is_set", "is set"], ["is_not_set", "is not set"],
];
export default function ConditionBuilder({ conditions, onChange, propertyOptions, propertyLoading }: {
  conditions: Condition[];
  onChange: (c: Condition[]) => void;
  // When supplied, the property field becomes a dropdown of these names (the selected
  // event's properties). Omit for a free-text property (Audience / Goal custom conditions).
  propertyOptions?: string[];
  propertyLoading?: boolean;
}) {
  const set = (i: number, patch: Partial<Condition>) => onChange(conditions.map((c, j) => j === i ? { ...c, ...patch } : c));
  const valueless = (op: string) => op === "is_set" || op === "is_not_set";
  const useDropdown = Array.isArray(propertyOptions);
  return (
    <div className="space-y-2">
      {conditions.map((c, i) => (
        <div key={i} className="rounded-lg border p-2 space-y-1.5" style={{ borderColor: "#E2E8F0" }}>
          <div className="flex items-center gap-1.5">
            {useDropdown ? (
              <select value={c.property} onChange={(e) => set(i, { property: e.target.value })}
                className="flex-1 text-xs border rounded-lg p-1.5" style={{ borderColor: "#E2E8F0" }}>
                <option value="">{propertyLoading ? "Loading properties…" : (propertyOptions!.length ? "Select a property" : "No properties found")}</option>
                {/* keep a previously-saved value visible even if it's not in the fetched list (ANTI-12) */}
                {c.property && !propertyOptions!.includes(c.property) && <option value={c.property}>{c.property}</option>}
                {propertyOptions!.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            ) : (
              <input type="text" value={c.property} onChange={(e) => set(i, { property: e.target.value })} placeholder="property"
                className="flex-1 text-xs border rounded-lg p-1.5" style={{ borderColor: "#E2E8F0" }} />
            )}
            <button type="button" aria-label="Remove condition" onClick={() => onChange(conditions.filter((_, j) => j !== i))} style={{ color: "#CBD5E0" }}><Trash2 size={13} /></button>
          </div>
          <div className="flex items-center gap-1.5">
            <select value={c.operator} onChange={(e) => set(i, { operator: e.target.value })} className="text-xs border rounded-lg p-1.5" style={{ borderColor: "#E2E8F0" }}>
              {CONDITION_OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {!valueless(c.operator) && (
              <input type="text" value={c.value} onChange={(e) => set(i, { value: e.target.value })} placeholder="value"
                className="flex-1 text-xs border rounded-lg p-1.5" style={{ borderColor: "#E2E8F0" }} />
            )}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...conditions, { property: "", operator: "eq", value: "" }])}
        className="w-full text-xs py-1.5 rounded-lg border border-dashed" style={{ borderColor: "#CBD5E0", color: "var(--brand-primary)" }}>+ Add condition</button>
    </div>
  );
}
